import { appendIf } from '@btime/helpers'
import { useTranslation } from 'react-i18next'

import { useColors } from '../../../packages/kiti/components/theme/_hooks/colors'
import {
  usePlaceDetailsAction,
  usePlaceEditAction,
  usePlaceLogAction,
  usePlaceLogsAction,
  usePlaceNewServiceOrderAction,
  usePlaceRemoveAction,
  usePlaceSendFormAction,
  usePlaceSendFormsAction,
  usePlacesRemoveAction,
  usePlaceTemplateRecordAction
} from './actions'
import { useOpenEditPlacesModal, useOpenGenerateActivatedQRCodesModal } from '../../routes/modals/modalsActions'
import { usePlaceType } from '../../../modules/places/_context/PlaceTypeContext'

const useSingleActions = (item, opts) => {
  const { t } = useTranslation('common')
  const hide = opts.hide || []
  const colors = useColors()

  const details = usePlaceDetailsAction(item)
  const log = usePlaceLogAction(item)
  const sendForm = usePlaceSendFormAction(item)
  const edit = usePlaceEditAction(item)
  const templateRecord = usePlaceTemplateRecordAction(item)
  const remove = usePlaceRemoveAction(item)
  const openGenerateActivatedQRCodesModal = useOpenGenerateActivatedQRCodesModal()
  const type = usePlaceType()

  return [
    ...appendIf(!hide.includes('seeDetails'), {
      value: t('seeDetails'),
      icon: 'BsSearch',
      onClick: details.click
    }),

    ...appendIf(templateRecord.hasPermission, {
      value: t('docGenerate'),
      icon: 'BsFileCheck',
      onClick: templateRecord.click
    }),

    ...appendIf(log.hasPermission, {
      value: t('logs'),
      icon: 'RiTimeLine',
      onClick: log.click,
      to: log.link
    }),

    ...appendIf(sendForm.hasPermission, {
      value: t('sendFormFull'),
      icon: 'BsReplyAll',
      onClick: sendForm.click,
      tourStep: 'sendForm'
    }),

    {
      value: t('generateQRCode'),
      icon: 'IoQrCodeOutline',
      onClick: () => openGenerateActivatedQRCodesModal({ ids: [item?.id], type })
    },

    ...appendIf(edit.hasPermission, {
      value: t('edit'),
      icon: 'BsPencil',
      onClick: edit.click
    }),

    ...appendIf(remove.hasPermission, {
      value: t('remove'),
      color: colors.red,
      icon: 'BsTrash',
      onClick: remove.click
    })
  ]
}

const useMultiActions = (ids, opts) => {
  const { t } = useTranslation('common')
  const hide = opts.hide || []
  const colors = useColors()

  const newServiceOrders = usePlaceNewServiceOrderAction(ids)
  const usePlaceSendFormAction = usePlaceSendFormsAction(ids)
  const logs = usePlaceLogsAction(ids)
  const removePlaces = usePlacesRemoveAction(ids)
  const openBulkModal = useOpenEditPlacesModal()
  const openGenerateActivatedQRCodesModal = useOpenGenerateActivatedQRCodesModal()

  const type = usePlaceType()

  return [
    ...appendIf(newServiceOrders.hasPermission, {
      value: t('createActivities'),
      icon: 'RiInboxLine',
      onClick: newServiceOrders.click
    }),

    {
      value: t('sendFormFull'),
      icon: 'BsReplyAll',
      tourStep: 'sendForm',
      onClick: usePlaceSendFormAction.click
    },

    ...appendIf(logs.hasPermission, {
      value: t('logs'),
      icon: 'RiTimeLine',
      onClick: logs.click,
      to: logs.link
    }),

    {
      value: t('generateQRCodes'),
      icon: 'IoQrCodeOutline',
      onClick: () => openGenerateActivatedQRCodesModal({ ids, type })
    },

    ...appendIf(ids?.length <= 20, {
      value: t('edit'),
      icon: 'BsPencil',
      onClick: () => openBulkModal({ type, ids })
    }),

    ...appendIf(removePlaces.hasPermission, {
      value: t('remove'),
      color: colors.red,
      icon: 'BsTrash',
      onClick: removePlaces.click
    })
  ]
}

export const usePlaceActionsList = (item, ids, opts = {}) => {
  const singleOptions = useSingleActions(item, opts)
  const multiOptions = useMultiActions(ids, opts)

  return [!ids || ids.length === 1 ? singleOptions : multiOptions, false]
}
